/**
 * Recipient risk confirmation (#144 / R2).
 *
 * Shown when the shared `recipientCodeGuard` predicate fires. It is a WARNING WITH A CHOICE, never a
 * block: the two cohorts that trigger it most often — counterfactual ERC-4337 accounts (the #137
 * target cohort) and, before the carve-out, EIP-7702 delegated EOAs — are legitimate users who must
 * be able to proceed.
 *
 * COPY DISCIPLINE. This guard's ceiling is "code exists at this address on some chain", never "the
 * user controls it" (same-address-different-controller across chains is real — cf.
 * Wintermute/Optimism). Nothing here may read as verification, and the `unverified` branch must not
 * imply the address is bad — it means we could not check, which is a different sentence.
 */
import { AlertTriangle } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_HEADING, FONT_BODY } from '@/config/theme'
import { CHAIN_REGISTRY } from '@/config/chains'
import type { RecipientCodeRisk } from '@/utils/recipientCodeGuard'
import type { Chain } from '@/types'

const chainLabel = (c: string): string =>
  CHAIN_REGISTRY[c as Chain]?.name ?? c.charAt(0).toUpperCase() + c.slice(1)

export interface RecipientRiskModalProps {
  risk: RecipientCodeRisk | null
  onProceed: () => void
  onCancel: () => void
}

export function RecipientRiskModal({ risk, onProceed, onCancel }: RecipientRiskModalProps) {
  const c = useThemeColors()
  if (!risk) return null

  const destName = risk.destChain ? chainLabel(risk.destChain) : 'the destination chain'
  const isUnverified = risk.verdict.reason === 'unverified'
  const withCode = risk.verdict.chainsWithCode.map(chainLabel)

  const title = isUnverified ? 'Could not check this address' : 'This address may not exist on ' + destName

  return (
    <Modal show onClose={onCancel} title={title}>
      <div style={{ padding: '16px 20px 20px' }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          background: c.iB, borderRadius: 12, padding: '12px 14px',
        }}>
          <AlertTriangle size={18} style={{ color: '#E8A33D', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: c.tx, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
            {isUnverified ? (
              <>
                We could not reach the networks needed to check whether this address exists on{' '}
                {destName}. This is usually a temporary network problem, <strong>not</strong> a
                problem with the address itself.
              </>
            ) : (
              <>
                This address is a <strong>smart contract</strong> on{' '}
                {withCode.length > 0 ? withCode.join(', ') : 'another network'}, but nothing is
                deployed at the same address on <strong>{destName}</strong>.
                {' '}Smart contract wallets are not guaranteed to exist at the same address on every
                network.
              </>
            )}
          </div>
        </div>

        <div style={{
          marginTop: 12,
          fontSize: 13, color: c.mu, fontFamily: FONT_BODY, lineHeight: 1.5,
        }}>
          If nothing can receive funds at this address on {destName}, the transfer{' '}
          <strong>cannot be reversed or recovered by us</strong>. Continue only if you know this
          address works on {destName} — for example a smart wallet you can deploy there, or an
          address you have already received funds at.
        </div>

        <div style={{
          marginTop: 12, padding: '10px 12px',
          background: c.iB, borderRadius: 10,
          fontSize: 12, color: c.tx,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          wordBreak: 'break-all', lineHeight: 1.4,
        }}>
          {risk.recipient}
        </div>

        {/* Honest about what was and was not established. */}
        <div style={{ marginTop: 10, fontSize: 11, color: c.mu, fontFamily: FONT_BODY, lineHeight: 1.45 }}>
          This check looks at whether code is deployed at the address. It cannot confirm who controls
          it.
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 2, padding: '12px 16px', borderRadius: 14, border: 'none',
              background: c.tx, color: c.card,
              fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING, cursor: 'pointer',
            }}
          >
            Go back
          </button>
          <button
            onClick={onProceed}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 14,
              border: `1px solid ${c.dv}`, background: 'transparent', color: c.mu,
              fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING, cursor: 'pointer',
            }}
          >
            Send anyway
          </button>
        </div>
      </div>
    </Modal>
  )
}
